import React, { useState } from 'react';
import {
  DocumentTextIcon,
  PencilSquareIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { SkeletonBlock } from '../../../../components/skeleton/skeleton';
import { TWButton, IconBadge } from '../../../../components/ui';
import companyApi from '../../../../services/companyApi';

const Notes = ({ companyDetail, updateCompany, setNotf }) => {
  const [loading, setLoading] = useState({ noteLoading: false, saving: false });
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({ note: companyDetail.note || '' });

  const isEmptyNote =
    !companyDetail?.note ||
    companyDetail.note.replace(/<[^>]*>/g, '').trim() === '';

  const onClickSave = async () => {
    try {
      setLoading((prev) => ({ ...prev, saving: true }));
      const body = { ...companyDetail, ...form };
      const formData = new FormData();
      Object.keys(body).forEach((key) => {
        if (body[key]) formData.append(key, body[key]);
      });
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      await companyApi.upsert(formData, config);
      setNotf((prev) => ({
        ...prev,
        message: 'Note Updated Successfully',
        display: true,
        type: 'success',
      }));
      setIsEditing(false);
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
        type: 'error',
      }));
    } finally {
      updateCompany();
      setLoading((prev) => ({ ...prev, saving: false }));
    }
  };

  const onCancel = () => {
    setForm({ note: companyDetail.note || '' });
    setIsEditing(false);
  };

  return (
    <section className="rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <IconBadge icon={DocumentTextIcon} tone="amber" size="md" />
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Company Notes
            </h2>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Internal notes visible only to admins.
            </p>
          </div>
        </div>
        {!isEditing && (
          <TWButton
            icon={PencilSquareIcon}
            label={isEmptyNote ? 'Add Note' : 'Edit'}
            onClick={() => setIsEditing(true)}
          />
        )}
      </div>

      <div className="px-6 py-6">
        {loading.noteLoading ? (
          <div className="space-y-3">
            <SkeletonBlock className="h-4 w-3/4" />
            <SkeletonBlock className="h-4 w-2/3" />
            <SkeletonBlock className="h-4 w-1/2" />
          </div>
        ) : isEditing ? (
          <div className="space-y-4">
            <div className="terms-editor rounded-md">
              <ReactQuill
                theme="snow"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e })}
                placeholder="Write a note about this company…"
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <TWButton
                label="Cancel"
                disabled={loading.saving}
                onClick={onCancel}
              />
              <TWButton
                variant="primary"
                icon={CheckIcon}
                label="Save Note"
                loading={loading.saving}
                disabled={loading.saving}
                onClick={onClickSave}
              />
            </div>
          </div>
        ) : isEmptyNote ? (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="flex w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 px-6 py-10 text-sm text-slate-500 transition hover:border-blue-400 hover:bg-blue-50/30 hover:text-blue-600 dark:border-slate-600 dark:text-slate-400 dark:hover:border-sky-400 dark:hover:bg-sky-500/5 dark:hover:text-sky-300"
          >
            <DocumentTextIcon className="h-8 w-8" />
            <span className="font-medium">Click to add a note</span>
          </button>
        ) : (
          <div
            className="prose prose-sm max-w-none text-slate-700 dark:prose-invert dark:text-slate-300"
            dangerouslySetInnerHTML={{ __html: companyDetail.note }}
          />
        )}
      </div>
    </section>
  );
};

export default Notes;
